<template>
    <PageContentLoader v-if="(fetching || stale) && !ring" />

    <PageErrorFiveHundred
        v-else-if="error"
        :errors="responseErrors"
    >
        <ThButton
            :loading="fetching"
            @click="executeQuery"
        >
            {{ t('globals.try_again') }}
        </ThButton>
    </PageErrorFiveHundred>

    <div
        v-else-if="ring"
        class="ring"
    >
        <ProductBreadcrumbs :breadcrumbs />

        <div
            :class="classProductWrapper"
            class="product-wrapper"
        >
            <ProductGalleryNew
                :product="ring"
                :see-in-store-cta="regionCode === 'UK'"
            />

            <div
                v-qa="'product-information'"
                class="product-details"
            >
                <div
                    :data-qa-product-properties="
                        JSON.stringify({
                            name: ring.productName,
                            metal: ring.primaryMetal.translation.name,
                            price: ring.salePrice.total.amount,
                        })
                    "
                    class="product-information"
                >
                    <div class="product-title">
                        <h1
                            v-qa="'ring-name'"
                            class="name"
                        >
                            {{ ring.productName }}
                        </h1>

                        <SaveForLaterButton
                            :product="ring"
                            page-type="pdp"
                        />
                    </div>

                    <ReviewsAggregate
                        v-if="ring.reviewsAggregate.total"
                        :data="ring.reviewsAggregate"
                    />

                    <p
                        v-qa="'ring-description'"
                        class="description"
                    >
                        {{ ring.translation.description }}
                    </p>

                    <TaxSnippet :data="ring.salePrice" />

                    <TooltipCustom page-type="product-details">
                        <!-- TODO translate -->
                        <template #label>Price explained</template>

                        <!-- TODO translate -->
                        <template #title>Price explained</template>

                        <!-- TODO translate -->
                        <template #content>
                            <ul class="price-explainer-list">
                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Ring price </span>

                                    <span class="value">
                                        <Money
                                            :data="ring.salePrice.total"
                                            :formatter
                                        />
                                    </span>
                                </li>

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Shipping </span>

                                    <!-- TODO translate -->
                                    <span class="value"> Free & Insured </span>
                                </li>

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Resizing </span>

                                    <!-- TODO translate -->
                                    <span class="value"> Complimentary </span>
                                </li>

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Warranty </span>

                                    <!-- TODO translate -->
                                    <span class="value"> Lifetime </span>
                                </li>

                                <hr class="divider" />

                                <li class="item">
                                    <!-- TODO translate -->
                                    <span class="label"> Total </span>

                                    <span class="value">
                                        <TaxSnippet
                                            :data="ring.salePrice"
                                            :inline="true"
                                        />
                                    </span>
                                </li>
                            </ul>
                        </template>
                    </TooltipCustom>

                    <FinanceOptions :affirm="ring.financeOptionsAffirm" />
                </div>

                <div class="product-customization">
                    <MetalVariations
                        :selected="ring"
                        :variations="ring.variations"
                    />

                    <div class="size-and-engraving">
                        <ThSelect
                            v-model="ringSizeId"
                            :error="ringSizeError"
                            :legend="'Ring size'"
                            :options="ringSizeOptions"
                            class="size"
                            serif-font
                        >
                            <template #tooltip>
                                <LazyTooltip
                                    inline
                                    path="/wedding-ring/ring-size"
                                    page-type="product-details"
                                />
                            </template>
                        </ThSelect>

                        <TextField
                            :id="'engraving'"
                            v-model="engraving"
                            :label="'Optional engraving'"
                            :maxlength="engravingMaxLength"
                            class="engraving"
                            serif-font
                            type="text"
                        >
                            <template #tooltip>
                                <LazyTooltip
                                    inline
                                    path="/wedding-ring/engraving"
                                    page-type="product-details"
                                />
                            </template>
                        </TextField>
                    </div>
                </div>

                <DeliveryTimeframe
                    v-if="deliveryDate"
                    :data="deliveryDate"
                />

                <DeliveryTimeframeExpress v-if="ring.supportsExpress" />

                <CtaBox :data="data?.pageProductDetails">
                    <template #default>
                        <AddToCartFloatingButton>
                            <template #button>
                                <div class="product-cta">
                                    <ThButton
                                        full-width
                                        :loading="fetchingExistingCustomer"
                                        type="solid"
                                        @click="handleAddToCart"
                                    >
                                        Add to cart
                                    </ThButton>
                                </div>
                            </template>
                        </AddToCartFloatingButton>
                    </template>

                    <template #below-cta>
                        <SaveForLaterButton
                            :product="ring"
                            below-cta
                            page-type="pdp"
                        />
                    </template>
                </CtaBox>

                <AuthFormInModal
                    :is-open="isEnquiryModalOpen"
                    :registration-disabled="fetchingNewCustomer"
                    :registration-errors="errors"
                    :content
                    @close="closeEnquiryModal"
                    @register="submitNewCustomer"
                    @login="handleAddToCart"
                >
                </AuthFormInModal>

                <PdpAccordion
                    :items="data?.pageProductDetails?.accordionItems ?? []"
                    about-this-entity-label="ring"
                    :resizeable="ring.isResizeable"
                    :product="ring"
                    :related-products="{
                        products: ring.relatedProducts,
                    }"
                >
                    <template #aboutThisProduct>
                        <div class="product-additional-details">
                            <div
                                v-if="ring?.translation?.seoText"
                                class="product-description"
                            >
                                <!-- eslint-disable vue/no-v-html -->
                                <p
                                    class="description"
                                    v-html="
                                        localizeHrefs(ring.translation.seoText)
                                    "
                                ></p>
                            </div>

                            <ProductProperties
                                v-if="productPropertiesData"
                                :data="productPropertiesData"
                            />

                            <AccentGems
                                v-if="ring?.accentGemItems.length"
                                :accent-gems="ring.accentGemItems"
                            />
                        </div>
                    </template>
                </PdpAccordion>
            </div>
        </div>

        <CmsBlocks
            :data="data?.pageProductDetails?.blocks"
            :product="ring"
            :related-products="{
                products: ring.relatedProducts,
            }"
            page-type="product-details"
        />

        <Reviews :data="ring.reviews" />
    </div>

    <PageErrorFourHundredFour v-else />
</template>

<script lang="ts" setup>
import {
    computed,
    defineAsyncComponent,
    hydrateOnIdle,
    hydrateOnVisible,
    ref,
    toRef,
    watch,
} from 'vue'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import {
    type GetWeddingRingQueryVariables,
    type MetalCode,
} from '@/graphql/api'

import { useProductPageMeta } from '../composables/useProductPageMeta'
import { useWeddingRingAddToCart } from './useWeddingRingAddToCart'
import { useResponseError } from '@/helpers/errors'
import { useProductAnalytics } from '../composables/analytics/useProductAnalytics'
import { useRingSize } from '@/domain/product/ring/useRingSize.ts'
import { getWeddingRingBreadcrumbs } from '@/views/product/wedding-ring/getWeddingRingBreadcrumbs.ts'
import { useRegionPlugin } from '@/plugins/region.ts'
import { useTranslator } from '@/i18n'
import { useDeliveryDate } from '@/views/product/composables/useDeliveryDate.ts'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { useMoneyFormatter } from '@/plugins/money-formatter.ts'
import { useHrefLocalizer } from '@/router/href-localizer'
import { useRoutes } from '@/router/routes.ts'
import { useGetWeddingRingQuery } from '@/views/product/wedding-ring/WeddingRing.generated.ts'

import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import ThButton from '@/components/ui/buttons/ThButton.vue'
import ThSelect from '@/components/ui/inputs/ThSelect.vue'
import TextField from '@/components/ui/inputs/TextField.vue'
import DeliveryTimeframe from '@/components/rings/DeliveryTimeframe.vue'
import FinanceOptions from '@/components/rings/FinanceOptions.vue'
import MetalVariations from '@/components/rings/MetalVariations.vue'
import TooltipCustom from '@/components/layout/tooltip/TooltipCustom.vue'
import TaxSnippet from '@/components/rings/TaxSnippet.vue'
import Money from '@/components/Money.vue'
import ProductGalleryNew from '@/components/layout/gallery/ProductGallery.vue'
import LazyTooltip from '@/components/layout/tooltip/LazyTooltip.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import ReviewsAggregate from '@/components/rings/ReviewsAggregate.vue'
import SaveForLaterButton from '@/components/saved-products/SaveForLaterButton.vue'
import PdpAccordion from '@/components/product/PdpAccordion.vue'
import AccentGems from '@/components/product/AccentGems.vue'
import ProductProperties, {
    type ProductPropertiesData,
} from '@/components/product/ProductProperties.vue'
import DeliveryTimeframeExpress from '@/components/rings/DeliveryTimeframeExpress.vue'
import AuthFormInModal from '@/components/user/auth/AuthFormInModal.vue'
import { useInit } from '@/plugins/init/use-init.ts'
import AddToCartFloatingButton from '../components/AddToCartFloatingButton.vue'
import CtaBox from '../components/CtaBox.vue'

const PageErrorFiveHundred = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFiveHundred.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnIdle(),
})

const Reviews = defineAsyncComponent({
    loader: () => import('@/components/rings/Reviews.vue'),
    hydrate: hydrateOnVisible(),
})

const { enquiryFormContent } = useInit()

export type WeddingRingProps = {
    slug: string
    metal: MetalCode
    suffixSlug: string
}

const props = defineProps<WeddingRingProps>()

const { t } = useI18n()
const currency = useCurrencyInput()
const formatter = useMoneyFormatter()
const localizeHrefs = useHrefLocalizer()
const engraving = ref('')
const hasEngraving = computed(() => Boolean(engraving.value.length))
const engravingMaxLength = 25

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const path = useRoute().path
const routes = useRoutes()
const redirect = useRedirect()
const router = useRouter()

const response = useGetWeddingRingQuery({
    variables: computed<GetWeddingRingQueryVariables>(() => ({
        slug: props.slug,
        metal: props.metal,
        suffixSlug: props.suffixSlug,
        locale: locale.value,
        currency: currency.value,
        region: regionCode.value,
        page: path,
        pageFallback: 'wedding-rings',
    })),
})

const deliveryDate = useDeliveryDate(response, (data) =>
    hasEngraving.value
        ? data.catalogueWeddingRing?.firstAvailableDeliveryDateWithEngraving
        : data.catalogueWeddingRing?.firstAvailableDeliveryDateWithoutEngraving
)

await response

const { fetching, data, error, stale, executeQuery } = response

const responseErrors = useResponseError(response)

const ring = toRef(() => data.value?.catalogueWeddingRing)

const redirectIfNeeded = async () => {
    if (
        ring.value &&
        (ring.value.slug !== props.slug ||
            ring.value.primaryMetal.systemCode !== props.metal ||
            ring.value.suffixSlug !== props.suffixSlug)
    ) {
        await redirect(
            router.resolve(routes.ring.wedding(ring.value)).href,
            301
        )
    }
}
watch(ring, redirectIfNeeded)

await redirectIfNeeded()

const productPropertiesData = computed<
    ProductPropertiesData | null | undefined
>(
    () =>
        ring.value && {
            title: 'Details',
            sections: [
                {
                    heading: 'Metal',
                    definition: ring.value.primaryMetal.translation.name,
                },
                {
                    heading: 'Band width',
                    definition: `${ring.value.bandWidth}mm`,
                },
            ],
        }
)

const {
    handleRingSizeNotSelected,
    ringSizeError,
    ringSizeId,
    ringSizeOptions,
} = useRingSize(toRef(() => ring.value?.sizes))

const {
    isEnquiryModalOpen,
    closeEnquiryModal,
    submitNewCustomer,
    fetchingNewCustomer,
    errors,
    fetchingExistingCustomer,
    handleAddToCart,
} = useWeddingRingAddToCart(ring, handleRingSizeNotSelected, engraving)

const breadcrumbs = getWeddingRingBreadcrumbs(
    {
        category: computed(() => ring.value?.categories[0]),
        metal: computed(() => ring.value?.primaryMetal),
        ringStyle: computed(() => ring.value?.ringStyles[0]),
        product: computed(() => ({
            title: ring.value?.productName || '',
        })),
    },
    routes
)

useProductAnalytics(ring, fetching)

useProductPageMeta(
    computed(() => response.data.value?.pageProductDetails?.meta),
    ring,
    fetching,
    stale
)

const classProductWrapper = computed(() => ({
    fetching: fetching.value,
}))

const content = computed(
    () => enquiryFormContent.value['wedding-rings-add-to-cart']
)
</script>

<style lang="scss" scoped>
.price-explainer-list {
    padding: 10px 15px;

    @include min-width(640) {
        padding: 0;
    }

    .item {
        margin-bottom: 15px;

        @include min-width(640) {
            display: flex;
            justify-content: space-between;
        }

        &:last-child {
            margin-bottom: 0;
        }

        .label {
            display: block;
            margin-bottom: 5px;
        }

        .value {
            display: block;
        }
    }

    .divider {
        @include border-bottom;

        margin-bottom: 15px;
    }
}

.product-additional-details {
    .product-description {
        font-size: 1rem;
        line-height: 1.6em;
        padding: 0 0 20px;
    }
}

.ring {
    .product-wrapper {
        @include product-wrapper();

        .product-details {
            .product-information {
                .price-explained {
                    background: transparent;
                    font-family: $sectra-font-stack;
                    display: flex;
                    align-items: center;
                    cursor: pointer;

                    &::after {
                        width: 22px;
                        height: 22px;
                        margin: 0 0 0 10px;
                        border-radius: 50%;
                        font-size: 18px;
                        font-family: $icon-font;
                        content: $icon-info;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }
                }
            }

            .product-customization {
                display: flex;
                align-items: flex-start;
                justify-content: space-between;
                flex-wrap: wrap;

                .product-variation {
                    width: 100%;
                    @include border-bottom;

                    padding: 0 0 15px;
                    margin: 0 0 25px;

                    .label {
                        display: block;
                        font-size: 14px;
                        line-height: 1em;
                        margin: 0 0 15px;
                    }
                }

                .size-and-engraving {
                    width: 100%;
                    border-bottom: 1px solid rgb(234 233 232);
                    padding: 0 0 25px;
                    margin: 0 0 25px;

                    @include min-width(640) {
                        display: flex;
                    }

                    .size {
                        @include min-width(640) {
                            width: 200px;
                        }
                    }

                    .engraving {
                        margin-top: 24px;

                        @include min-width(640) {
                            margin: 0 0 0 20px;
                        }
                    }
                }
            }
        }
    }
}
</style>
